// abbreviation/abbreviationediting.js

import { Plugin, Command } from "@ckeditor/ckeditor5-core";
import { Widget, toWidget, toWidgetEditable } from '@ckeditor/ckeditor5-widget';

class InsertSimpleBoxCommand extends Command {
  execute() {
    this.editor.model.change((writer) => {
      // Insert <simpleBox>*</simpleBox> at the current selection position
      // in a way that will result in creating a valid model structure.
      this.editor.model.insertObject(createSimpleBox(writer));
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const allowedIn = model.schema.findAllowedParent(
      selection.getFirstPosition(),
      "simpleBox1"
    );

    this.isEnabled = allowedIn !== null;
  }
}

function createSimpleBox(writer) {
  const simpleBox = writer.createElement("simpleBox1");
  const simpleBoxTitle = writer.createElement("simpleBoxTitle1");
  const simpleBoxSubTitle = writer.createElement("simpleBoxSubTitle1");
  const simpleBoxDescription = writer.createElement("simpleBoxDescription1");

  writer.append(simpleBoxTitle, simpleBox);
  writer.append(simpleBoxSubTitle, simpleBox);
  writer.append(simpleBoxDescription, simpleBox);

  // There must be at least one paragraph for the description to be editable.
  // See https://github.com/ckeditor/ckeditor5/issues/1464.
  writer.appendElement("paragraph", simpleBoxDescription);

  return simpleBox;
}

export default class SimpleBoxEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    console.log("SimpleBoxEditing#init() got called");

    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add(
      "insertSimpleBox1",
      new InsertSimpleBoxCommand(this.editor)
    );
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register("simpleBox1", {
      // Behaves like a self-contained block object (e.g. a block image)
      // allowed in places where other blocks are allowed (e.g. directly in the root).
      inheritAllFrom: "$blockObject",
    });

    schema.register("simpleBoxTitle1", {
      // Cannot be split or left by the caret.
      isLimit: true,

      allowIn: "simpleBox1",

      // Allow content which is allowed in blocks (i.e. text with attributes).
      allowContentOf: "$block",
    });

    schema.register("simpleBoxSubTitle1", {
      // Cannot be split or left by the caret.
      isLimit: true,

      allowIn: "simpleBox1",

      // Allow content which is allowed in blocks (i.e. text with attributes).
      allowContentOf: "$block",
    });

    schema.register("simpleBoxDescription1", {
      // Cannot be split or left by the caret.
      isLimit: true,

      allowIn: "simpleBox1",

      // Allow content which is allowed in the root (e.g. paragraphs).
      allowContentOf: "$root",
    });

    schema.addChildCheck((context, childDefinition) => {
      if (
        context.endsWith("simpleBoxDescription") &&
        childDefinition.name == "simpleBox1"
      ) {
        return false;
      }
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;

    // <simpleBox> converters
    conversion.for("upcast").elementToElement({
      model: "simpleBox1",
      view: {
        name: "section",
        classes: "simple-box1",
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: "simpleBox1",
      view: {
        name: "section",
        classes: "simple-box1",
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: "simpleBox1",
      view: (modelElement, { writer: viewWriter }) => {
        const section = viewWriter.createContainerElement("section", {
          class: "simple-box1",
        });

        return toWidget(section, viewWriter, { label: "simple box widget" });
      },
    });

    // <simpleBoxTitle> converters
    conversion.for("upcast").elementToElement({
      model: "simpleBoxTitle1",
      view: {
        name: "div",
        classes: "simple-box-title1",
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: "simpleBoxTitle1",
      view: {
        name: "div",
        classes: "simple-box-title1",
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: "simpleBoxTitle1",
      view: (modelElement, { writer: viewWriter }) => {
        // Note: You use a more specialized createEditableElement() method here.
        const h1 = viewWriter.createEditableElement("div", {
          class: "simple-box-title1",
        });

        return toWidgetEditable(h1, viewWriter);
      },
    });

    // <simpleBoxTitle> converters
    conversion.for("upcast").elementToElement({
      model: "simpleBoxSubTitle1",
      view: {
        name: "div",
        classes: "simple-box-subtitle1",
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: "simpleBoxSubTitle1",
      view: {
        name: "div",
        classes: "simple-box-subtitle1",
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: "simpleBoxSubTitle1",
      view: (modelElement, { writer: viewWriter }) => {
        // Note: You use a more specialized createEditableElement() method here.
        const h1 = viewWriter.createEditableElement("div", {
          class: "simple-box-subtitle1",
        });

        return toWidgetEditable(h1, viewWriter);
      },
    });

    // <simpleBoxDescription> converters
    conversion.for("upcast").elementToElement({
      model: "simpleBoxDescription1",
      view: {
        name: "div",
        classes: "simple-box-description1",
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: "simpleBoxDescription1",
      view: {
        name: "div",
        classes: "simple-box-description1",
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: "simpleBoxDescription1",
      view: (modelElement, { writer: viewWriter }) => {
        // Note: You use a more specialized createEditableElement() method here.
        const div = viewWriter.createEditableElement("div", {
          class: "simple-box-description1",
        });

        return toWidgetEditable(div, viewWriter);
      },
    });
  }
}
